/**
 * Frontend Telemetry Service
 * Uses Application Insights for error tracking, performance monitoring, and analytics
 */

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

// Application Insights configuration
const connectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;

// React plugin for component tracking
const reactPlugin = new ReactPlugin();

// Application Insights instance
let appInsights = null;

/**
 * Initialize Application Insights
 */
export function initializeTelemetry(history = null) {
  if (!connectionString) {
    console.log('[Telemetry] Application Insights not configured - using console logging only');
    return;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: { history }
        },
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        correlationHeaderExcludedDomains: [
          '*.clerk.accounts.dev',
          '*.clerk.com',
          'clerk.com',
          '*.stripe.com',
          'stripe.com'
        ],
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxPerfTracking: true,
        isBrowserLinkTrackingEnabled: true,
        enableUnhandledPromiseRejectionTracking: true,
        disableFetchTracking: false,
        autoTrackPageVisitTime: true,
        disableExceptionTracking: false
      }
    });

    appInsights.loadAppInsights();
    appInsights.trackPageView();

    console.log('[Telemetry] Application Insights initialized');
  } catch (error) {
    console.error('[Telemetry] Failed to initialize Application Insights:', error);
  }
}

/**
 * Get the React plugin for component tracking
 */
export function getReactPlugin() {
  return reactPlugin;
}

/**
 * Get the Application Insights instance
 */
export function getAppInsights() {
  return appInsights;
}

/**
 * Main telemetry logger
 */
export const telemetry = {
  /**
   * Track a page view
   */
  trackPageView(name, uri, properties = {}) {
    console.log('[Telemetry] Page view:', name, uri);
    if (appInsights) {
      appInsights.trackPageView({ name, uri, properties });
    }
  },

  /**
   * Track a custom event
   */
  trackEvent(name, properties = {}, measurements = {}) {
    console.log('[Telemetry] Event:', name, properties);
    if (appInsights) {
      appInsights.trackEvent({ name, properties, measurements });
    }
  },

  /**
   * Track an exception/error
   */
  trackException(error, properties = {}) {
    console.error('[Telemetry] Exception:', error.message, properties);
    if (appInsights) {
      appInsights.trackException({
        exception: error,
        properties
      });
    }
  },

  /**
   * Track a trace/log message
   */
  trackTrace(message, severity = 'info', properties = {}) {
    const severityMap = {
      verbose: 0,
      info: 1,
      warning: 2,
      error: 3,
      critical: 4
    };

    console.log(`[Telemetry] Trace (${severity}):`, message);
    if (appInsights) {
      appInsights.trackTrace({
        message,
        severityLevel: severityMap[severity] || 1,
        properties
      });
    }
  },

  /**
   * Track a metric
   */
  trackMetric(name, value, properties = {}) {
    console.log('[Telemetry] Metric:', name, value);
    if (appInsights) {
      appInsights.trackMetric({ name, average: value, properties });
    }
  },

  /**
   * Track dependency call (API requests)
   */
  trackDependency(name, duration, success, properties = {}) {
    console.log('[Telemetry] Dependency:', name, duration, success);
    if (appInsights) {
      appInsights.trackDependencyData({
        name,
        duration,
        success,
        responseCode: success ? 200 : 500,
        type: 'HTTP',
        properties
      });
    }
  },

  /**
   * Set the authenticated user context
   */
  setAuthenticatedUser(userId, accountId = null) {
    console.log('[Telemetry] Setting user context:', userId);
    if (appInsights) {
      appInsights.setAuthenticatedUserContext(userId, accountId, true);
    }
  },

  /**
   * Clear the authenticated user context
   */
  clearAuthenticatedUser() {
    console.log('[Telemetry] Clearing user context');
    if (appInsights) {
      appInsights.clearAuthenticatedUserContext();
    }
  },

  /**
   * Flush all pending telemetry
   */
  flush() {
    if (appInsights) {
      appInsights.flush();
    }
  }
};

/**
 * User action tracking helpers
 */
export const userActions = {
  // Authentication events
  signInStarted: () => telemetry.trackEvent('auth_sign_in_started'),
  signInCompleted: (userId) => {
    telemetry.trackEvent('auth_sign_in_completed', { userId });
    telemetry.setAuthenticatedUser(userId);
  },
  signInFailed: (error) => telemetry.trackEvent('auth_sign_in_failed', { error: error.message }),
  signOut: () => {
    telemetry.trackEvent('auth_sign_out');
    telemetry.clearAuthenticatedUser();
  },

  // Application flow events
  applicationCreated: (applicationId) => telemetry.trackEvent('application_created', { applicationId }),
  applicationUpdated: (applicationId, field) => telemetry.trackEvent('application_updated', { applicationId, field }),
  applicationDeleted: (applicationId) => telemetry.trackEvent('application_deleted', { applicationId }),

  // Trademark events
  trademarkUploaded: (applicationId, type) => telemetry.trackEvent('trademark_uploaded', { applicationId, type }),
  trademarkAnalysisStarted: (applicationId) => telemetry.trackEvent('trademark_analysis_started', { applicationId }),
  trademarkAnalysisCompleted: (applicationId, duration) => telemetry.trackEvent('trademark_analysis_completed', { applicationId }, { duration }),

  // AI Chat events
  chatMessageSent: (applicationId, messageLength) => telemetry.trackEvent('chat_message_sent', { applicationId }, { messageLength }),
  chatResponseReceived: (applicationId, duration) => telemetry.trackEvent('chat_response_received', { applicationId }, { duration }),

  // Payment events
  checkoutStarted: (applicationId, amount) => telemetry.trackEvent('checkout_started', { applicationId }, { amount }),
  checkoutCompleted: (applicationId, amount) => telemetry.trackEvent('checkout_completed', { applicationId }, { amount }),
  checkoutFailed: (applicationId, error) => telemetry.trackEvent('checkout_failed', { applicationId, error }),

  // Country selection events
  countrySelected: (applicationId, countryCode) => telemetry.trackEvent('country_selected', { applicationId, countryCode }),
  countryDeselected: (applicationId, countryCode) => telemetry.trackEvent('country_deselected', { applicationId, countryCode }),

  // UI interactions
  tabChanged: (tabName) => telemetry.trackEvent('tab_changed', { tabName }),
  modalOpened: (modalName) => telemetry.trackEvent('modal_opened', { modalName }),
  modalClosed: (modalName) => telemetry.trackEvent('modal_closed', { modalName }),
  buttonClicked: (buttonName, context = {}) => telemetry.trackEvent('button_clicked', { buttonName, ...context })
};

/**
 * Performance tracking helpers
 */
export const performance = {
  /**
   * Measure and track the duration of an operation
   */
  async measure(name, operation, properties = {}) {
    const startTime = Date.now();
    try {
      const result = await operation();
      const duration = Date.now() - startTime;
      telemetry.trackMetric(`${name}_duration`, duration, properties);
      telemetry.trackEvent(`${name}_completed`, { ...properties, success: true }, { duration });
      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      telemetry.trackEvent(`${name}_failed`, { ...properties, error: error.message }, { duration });
      telemetry.trackException(error, { operation: name, ...properties });
      throw error;
    }
  },

  /**
   * Track page load performance
   */
  trackPageLoad() {
    if (typeof window !== 'undefined' && window.performance) {
      const timing = window.performance.timing;
      const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
      const domReadyTime = timing.domContentLoadedEventEnd - timing.navigationStart;
      const dnsTime = timing.domainLookupEnd - timing.domainLookupStart;
      const tcpTime = timing.connectEnd - timing.connectStart;
      const serverTime = timing.responseEnd - timing.requestStart;

      telemetry.trackMetric('page_load_time', pageLoadTime);
      telemetry.trackMetric('dom_ready_time', domReadyTime);
      telemetry.trackMetric('dns_lookup_time', dnsTime);
      telemetry.trackMetric('tcp_connect_time', tcpTime);
      telemetry.trackMetric('server_response_time', serverTime);
    }
  }
};

/**
 * Error boundary helper for React components
 */
export function logComponentError(error, errorInfo) {
  telemetry.trackException(error, {
    componentStack: errorInfo?.componentStack,
    type: 'react_error_boundary'
  });
}

export default telemetry;
